export default class Plot {
  constructor(maximumAcceptableDowntime, recoveryTimeObjective, brandColors) {
    this.maximumAcceptableDowntime = maximumAcceptableDowntime;
    this.recoveryTimeObjective = recoveryTimeObjective;
    this.brandColors = brandColors;
  }

  getPlotBands(instructionText) {
    const recoveryTimeObjectivePlotLineLabelXValue = this.getRecoveryTimeObjectivePlotLineLabelXValue();
    const maximumAcceptableDowntimePlotLineLabelXValue = this.getMaximumAcceptableDowntimePlotLineLabelXValue();
    const recoveryTimeObjectivePlotLine = this.getRecoveryTimeObjectivePlotLine(
      recoveryTimeObjectivePlotLineLabelXValue
    );
    const maximumAcceptableDowntimePlotLine = this.getMaximumAcceptableDowntimePlotLine(
      maximumAcceptableDowntimePlotLineLabelXValue
    );
    const plotLines = [];

    if (recoveryTimeObjectivePlotLine) {
      plotLines.push(recoveryTimeObjectivePlotLine);
    }
    if (maximumAcceptableDowntimePlotLine) {
      plotLines.push(maximumAcceptableDowntimePlotLine);
    }

    // Rotate label if RTO < 2 so the text can fully fit
    const targetRotation = this.getTargetRotation();
    const targetYPosition = this.getTargetYPosition();
    const acceptableRotation = this.getAcceptableRotation();
    const acceptableYPosition = this.getAcceptableYPosition();
    const unacceptableRotation = this.getUnacceptableRotation();
    const unacceptableYPosition = this.getUnacceptableYPosition();
    const noMADPlotBand = this.getNoMADPlotBand(instructionText);
    const targetPlotBand = this.getTargetPlotBand(targetRotation, targetYPosition);
    const acceptablePlotBand = this.getAcceptablePlotBand(acceptableRotation, acceptableYPosition);
    const unacceptablePlotBand = this.getUnacceptablePlotBand(unacceptableRotation, unacceptableYPosition);
    return {
      plotLines, noMADPlotBand, targetPlotBand, acceptablePlotBand, unacceptablePlotBand
    };
  }

  getUnacceptablePlotBand(unacceptableRotation, unacceptableYPosition) {
    return (
      this.maximumAcceptableDowntime > 9.5
      || (
        this.recoveryTimeObjective < 0.5
        && this.maximumAcceptableDowntime < 0.5
      )
    )
      ? null
      : {
        color: this.brandColors.bankersLoveRed.$25,
        from: this.maximumAcceptableDowntime,
        to: 10,
        label: {
          style: {
            fontFamily: '\'Nunito\', sans-serif',
            fontSize: 16,
          },
          rotation: unacceptableRotation,
          y: unacceptableYPosition,
        },
      };
  }

  getAcceptablePlotBand(acceptableRotation, acceptableYPosition) {
    return (
      this.maximumAcceptableDowntime < 0.5
      || this.recoveryTimeObjective > 9.5
    )
      ? null
      : {
        color: this.brandColors.navantaYellow.$25,
        from: this.recoveryTimeObjective,
        to: this.maximumAcceptableDowntime,
        label: {
          style: {
            fontFamily: '\'Nunito\', sans-serif',
            fontSize: 16,
          },
          rotation: acceptableRotation,
          y: acceptableYPosition,
        },
      };
  }

  getTargetPlotBand(targetRotation, targetYPosition) {
    return this.recoveryTimeObjective < 0.5
      ? null
      : {
        color: this.brandColors.hulaSkirtGreen.$25,
        from: 0,
        to: this.recoveryTimeObjective,
        label: {
          style: {
            fontFamily: '\'Nunito\', sans-serif',
            fontSize: 16,
          },
          rotation: targetRotation,
          text: 'Target Recovery',
          y: targetYPosition,
        },
      };
  }

  getNoMADPlotBand(instructionText) {
    return this.maximumAcceptableDowntime > 0
      ? null
      : {
        color: this.brandColors.navantaCharcoal.$5,
        from: 0,
        to: 10,
        label: {
          style: {
            fontFamily: '\'Nunito\', sans-serif',
            fontSize: 22,
            fontWeight: 'bold',
            color: this.brandColors.navantaCharcoal.$55,
          },
          text: instructionText,
          y: 130,
        },
      };
  }

  getUnacceptableYPosition() {
    return this.maximumAcceptableDowntime > 7
      ? 130
      : 80;
  }

  getUnacceptableRotation() {
    return this.maximumAcceptableDowntime > 7
      ? 90
      : 0;
  }

  getAcceptableYPosition() {
    return (
      (this.maximumAcceptableDowntime - this.recoveryTimeObjective) < 3
    )
      ? 130
      : 80;
  }

  getAcceptableRotation() {
    return (
      (this.maximumAcceptableDowntime - this.recoveryTimeObjective) < 3
    )
      ? 90
      : 0;
  }

  getTargetYPosition() {
    return this.recoveryTimeObjective < 2.5
      ? 130
      : 80;
  }

  getTargetRotation() {
    return this.recoveryTimeObjective < 2.5
      ? 90
      : 0;
  }

  getMaximumAcceptableDowntimePlotLine(maximumAcceptableDowntimePlotLineLabelXValue) {
    return this.maximumAcceptableDowntime < 0.5
      ? null
      : {
        color: this.brandColors.bankersLoveRed.$100,
        dashStyle: 'Solid',
        value: this.maximumAcceptableDowntime,
        width: 5,
        zIndex: 2,
        label: {
          text: 'Maximum Allowable Downtime',
          x: maximumAcceptableDowntimePlotLineLabelXValue,
          y: 20,
        },
      };
  }

  getRecoveryTimeObjectivePlotLine(recoveryTimeObjectivePlotLineLabelXValue) {
    return (
      this.recoveryTimeObjective < 0.5
      || this.maximumAcceptableDowntime < 0.5
      || this.recoveryTimeObjective === this.maximumAcceptableDowntime
    )
      ? null
      : {
        color: this.brandColors.navantaCoral.$100,
        dashStyle: 'Solid',
        value: this.recoveryTimeObjective,
        width: 5,
        zIndex: 2,
        label: {
          text: 'Recovery Time Objective',
          x: recoveryTimeObjectivePlotLineLabelXValue,
          y: 20,
        },
      };
  }

  getMaximumAcceptableDowntimePlotLineLabelXValue() {
    return this.maximumAcceptableDowntime > 9
      ? -30
      : 20;
  }

  getRecoveryTimeObjectivePlotLineLabelXValue() {
    return (
      (this.maximumAcceptableDowntime - this.recoveryTimeObjective) < 1
    )
      ? -30
      : 20;
  }
}
